import React, { useEffect } from 'react'
import styled from 'styled-components'

// Utils
import { setSketchUrlScheme } from 'utils'

// Constants
import { SKETCH_WEBSITE } from 'constants/sketchWebsite'

// Components
import Modal from 'components/Modal'
import Button, { flatButtonStyles } from 'components/Button'
import { ModalInjectedProps } from 'containers/ModalManager'

import { downloadFile } from 'components/DownloadFile'

// Possible actions to launch Sketch app
const sketchActions = {
  addLibrary: (shareId: string) =>
    setSketchUrlScheme(`add-library/cloud/${shareId}`),
  openInSketch: (shareId: string) =>
    setSketchUrlScheme(`sketch.cloud/s/${shareId}`),
}

type SketchAction = keyof typeof sketchActions

type LaunchSketchType = {
  shareId: string
  action: SketchAction
}

// Custom hook to group the logic to launch the Sketch app
function useSketchUrlSchema({ shareId, action }: LaunchSketchType) {
  useEffect(() => {
    const getSketchActionUrl = (
      shareId: string,
      action: SketchAction
    ): string => sketchActions[action](shareId)

    downloadFile(getSketchActionUrl(shareId, action))
  }, [shareId, action])
}

interface LaunchSketchAlertProps extends ModalInjectedProps {
  shareId: string
  action: SketchAction
}

const LaunchSketchAlert: React.FC<LaunchSketchAlertProps> = ({
  shareId,
  hideModal,
  action,
}) => {
  useSketchUrlSchema({ shareId, action })

  const Anchor = styled.a.attrs({
    variant: 'primary',
    intent: 'primary',
  })`
    ${flatButtonStyles};
    padding: 0;
  `

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Launching the Sketch Mac App…</Modal.Header>
      <Modal.Body>
        If nothing happens, you may need to{' '}
        <Anchor
          href={`${SKETCH_WEBSITE}/get`}
          target="_blank"
          rel="noopener noreferrer">
          install the Mac app
        </Anchor>{' '}
        and try again.
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}

export { LaunchSketchAlert }
